import { useCallback, useEffect, useMemo, useState, type Dispatch, type SetStateAction } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { useAppDispatch, useAppSelector } from '../../../../hooks/redux'
import UseUserType, { type UserType } from '../../../../hooks/UseUserType'
import useMediaQuery from '../../../../hooks/useMediaQuery'
import { getBelongsUnitForOffer, getBelongsUnitForViewing } from '../../../../functions/shared/schedule/getBelongsUnit'
import { filterAndSortOffers } from '../filters/filterAndSortOffers'
import { filterAndSortViewings } from '../filters/filterAndSortViewings'
import { filterAndSortAdds } from '../filters/filterAndSortAdds'

import { type RootState } from '../../../../store/store'
import { mainButtonClosing, setShowPopupScheduleFilters, setWhereAfterToGo } from '../../../../store/slices/modalSlice'

import { REACT_APP_BSO_USER_ID, USER_ID_KEY } from '../../../../urls'
import { type IViewing } from '../../../../store/slices/viewingsSlice'
import { type IOffer } from '../../../../store/slices/offersSlice'
import { type IAdvertise } from '../../../../store/slices/advertisesSlice'
import { fetchPortalOrdersFromApiV2, mapPortalBitrixRequestsFromOrders, type PortalBitrixRequest } from '../../../../functions/shared/orders/portalBitrixRequests'
import { buildAgentActivity } from '../agentActivity/buildAgentActivity'
import { type AgentActivityCard, type AgentActivityTaskItem } from '../agentActivity/agentActivity.types'
import { type User } from '../../../../store/slices/userSlice'

export type ScheduleAccordionKey = string | false

export interface ScheduleViewModel {
    pathname: string
    isDesktop: boolean
    isWithSidebar: boolean
    loading: boolean
    userDevice: string
    filterNavigate: string
    scheduleFiltersApply: boolean
    setScheduleFiltersApply: Dispatch<SetStateAction<boolean>>
    expanded: ScheduleAccordionKey
    setExpanded: Dispatch<SetStateAction<ScheduleAccordionKey>>
    filteredViewings: IViewing[]
    filteredOffers: IOffer[]
    filteredAdvertises: IAdvertise[]
    viewingsNotifies: any[]
    offersNotifies: any[]
    advertisesNotifies: any[]
    portalViewingRequests: PortalBitrixRequest[]
    portalOfferRequests: PortalBitrixRequest[]
    portalAllRequests: PortalBitrixRequest[]
    agentActivityCards: AgentActivityCard[]
    allAttention: any[]
    isThereNotices: boolean
    filtersBottomOffset?: string
    mainButtonOpen: boolean
    showPopupScheduleFilters: boolean
    openAgentActivityItem: (item: AgentActivityTaskItem) => void
    openFiltersPopup: () => void
    closeFiltersPopup: () => void
}

interface UseScheduleVMArgs {
    isWithSidebar: boolean
}

export function useScheduleVM({ isWithSidebar }: UseScheduleVMArgs): ScheduleViewModel {
    useParams()
    const { pathname } = useLocation()
    const navigate = useNavigate()
    const isDesktop = useMediaQuery('(min-width: 992px)')
    const { userType, userTypeLowercase } = UseUserType()
    let notifyApp: UserType | 'BSO' = userType

    const dispatch = useAppDispatch()

    const currentUser = useAppSelector((state: RootState) => state.user.currentUser)
    const userID = currentUser.id ?? window.localStorage.getItem(USER_ID_KEY)
    if (String(userID) === REACT_APP_BSO_USER_ID) {
        notifyApp = 'BSO'
    }

    const userDevice = useAppSelector((state) => state.app.userDevice)
    const scheduleFilters = useAppSelector((state: RootState) => state.modal.scheduleFilters)
    const filterNavigate = useAppSelector((state: RootState) => state.schedule.filterNavigate)

    const [scheduleFiltersApply, setScheduleFiltersApply] = useState(false)
    const [expanded, setExpanded] = useState<ScheduleAccordionKey>(false)
    const [portalOrders, setPortalOrders] = useState<any[]>([])

    const mainButtonOpen = useAppSelector((state: RootState) => state.modal.mainButtonOpen)
    const showPopupScheduleFilters = useAppSelector((state: RootState) => state.modal.showPopupScheduleFilters)
    const loading = useAppSelector((state: RootState) => state.user.loading)

    const viewings = useAppSelector((state: RootState) => state.viewings.viewings)
    const offers = useAppSelector((state: RootState) => state.offers.offers)
    const advertises = useAppSelector((state: RootState) => state.advertises.advertises)
    const allUsers = useAppSelector((state: RootState) => state.user.allUsers)
    const units = useAppSelector((state: RootState) => state.units.units)

    useEffect(() => {
        dispatch(mainButtonClosing())
    }, [dispatch])

    useEffect(() => {
        if (notifyApp !== 'BSO') {
            setPortalOrders([])
            return
        }

        let isCancelled = false
        const loadPortalOrders = async () => {
            try {
                const fetchedOrders = await fetchPortalOrdersFromApiV2()
                if (!isCancelled) {
                    setPortalOrders(fetchedOrders)
                }
            } catch (error) {
                if (!isCancelled) {
                    setPortalOrders([])
                }
                console.error('Failed to fetch portal requests orders', error)
            }
        }

        void loadPortalOrders()

        return () => {
            isCancelled = true
        }
    }, [notifyApp])

    const filteredViewings = useMemo(() => viewings.filter((view: IViewing) => {
        const ownerUnit = view?.attributes?.Unit?.data?.attributes?.User?.data?.id
        return ownerUnit === Number(userID) && filterAndSortViewings(view?.attributes?.Unit?.data, view, scheduleFilters)
    }), [viewings, userID, scheduleFilters])

    const filteredOffers = useMemo(() => offers.filter((offer: IOffer) => {
        const ownerUnit = offer?.attributes?.Unit?.data?.attributes?.User?.data?.id
        return ownerUnit === Number(userID)
            && (offer?.attributes?.Type?.[0]?.Offer)
            && filterAndSortOffers(offer?.attributes?.Unit?.data, offer, scheduleFilters)
    }), [offers, userID, scheduleFilters])

    const filteredAdvertises = useMemo(() => advertises.filter((advertise) => {
        const ownerUnit = advertise?.attributes?.Unit?.data?.attributes?.User?.data?.id

        return ownerUnit === Number(userID)
            && filterAndSortAdds(advertise?.attributes?.Unit?.data, advertise, scheduleFilters)
    }), [advertises, userID, scheduleFilters])

    const offersNotifies = useMemo(() =>
        filteredOffers.filter((el: any) => {
            const task = getBelongsUnitForOffer(el)
            return (
                (el?.attributes?.Notify === notifyApp || el?.attributes?.Notify === 'Both')
                && task.type === 'BSO UNIT'
                && el?.attributes?.User?.data?.id !== Number(userID)
            )
        }), [filteredOffers, notifyApp, userID]
    )

    const viewingsNotifies = useMemo(() =>
        filteredViewings.filter((el: any) => {
            const task = getBelongsUnitForViewing(el)
            return (
                (el?.attributes?.Notify === notifyApp || el?.attributes?.Notify === 'Both')
                && task.type === 'MY UNIT'
                && el?.attributes?.User?.data?.id !== Number(userID)
            )
        }), [filteredViewings, notifyApp, userID]
    )

    const advertisesNotifies = useMemo(() =>
        filteredAdvertises.filter((el: any) =>
            (el?.attributes?.Notify === notifyApp || el?.attributes?.Notify === 'Both')
            && el?.attributes?.Unit?.data?.id
        ), [filteredAdvertises, notifyApp]
    )

    const portalAllRequests = useMemo(() => {
        if (notifyApp !== 'BSO') return []
        return mapPortalBitrixRequestsFromOrders(portalOrders, allUsers as any[], units as any[])
    }, [notifyApp, portalOrders, allUsers, units])

    const portalViewingRequests = useMemo(
        () => portalAllRequests.filter((item) => item.requestType === 'viewing'),
        [portalAllRequests]
    )

    const portalOfferRequests = useMemo(
        () => portalAllRequests.filter((item) => item.requestType === 'offer'),
        [portalAllRequests]
    )

    const viewingRouteById = useMemo(() => {
        const routes: Record<number, string> = {}
        filteredViewings.forEach((viewing) => {
            const unitId = viewing?.attributes?.Unit?.data?.id
            if (unitId == null) return

            const task = getBelongsUnitForViewing(viewing)
            const currentUnit = units.find((unit) => Number(unit.id) === Number(unitId))

            routes[Number(viewing.id)] = userTypeLowercase === 'agent'
                ? `/${userTypeLowercase}/modalView/viewing_modal/${String(unitId)}/${String(viewing.id)}/${task.type === 'MY UNIT' ? 'my_unit' : currentUnit?.attributes?.Statuses?.includes('Door close') ? 'other_agent_s_unit' : 'calendar'}`
                : `/${userTypeLowercase}/modalView/${notifyApp === 'BSO' ? 'magic_' : ''}viewing_modal/${String(unitId)}/${String(viewing.id)}/calendar`
        })

        return routes
    }, [filteredViewings, units, userTypeLowercase, notifyApp])

    const offerRouteById = useMemo(() => {
        const routes: Record<number, string> = {}
        filteredOffers.forEach((offer) => {
            const unitId = offer?.attributes?.Unit?.data?.id
            if (unitId == null) return
            const task = getBelongsUnitForOffer(offer)
            const typeOffer = offer?.attributes?.Type?.[0]?.__component?.split('.')[1]

            routes[Number(offer.id)] = notifyApp === 'BSO'
                ? `/${userTypeLowercase}/modalView/magic_offer_modal/${String(unitId)}/${String(offer.id)}/catalog?type=${String(offer?.attributes?.Type?.[0]?.__component ?? 'not')}`
                : `/${userTypeLowercase}/modalView/offer_modal/${String(unitId)}/${String(offer.id)}/${task.redirect}?type=${typeOffer ?? 'not'}`
        })
        return routes
    }, [filteredOffers, notifyApp, userTypeLowercase])

    const advertiseRouteById = useMemo(() => {
        const routes: Record<number, string> = {}
        filteredAdvertises.forEach((advertise) => {
            const unitId = advertise?.attributes?.Unit?.data?.id
            if (unitId == null) return
            routes[Number(advertise.id)] = `/${userTypeLowercase}/modalView/${notifyApp === 'BSO' ? 'magic_' : ''}advertise_modal/${String(unitId)}/${String(advertise.id)}/tasks`
        })
        return routes
    }, [filteredAdvertises, userTypeLowercase, notifyApp])

    const portalRouteByOrderId = useMemo(() => {
        const routes: Record<number, string> = {}
        portalAllRequests.forEach((request) => {
            routes[Number(request.orderId)] = `/${userTypeLowercase}/portal_request_actions/${String(request.orderId)}`
        })
        return routes
    }, [portalAllRequests, userTypeLowercase])

    const agentActivityCards = useMemo(() => buildAgentActivity({
        allUsers: allUsers as User[],
        viewings: filteredViewings,
        offers: filteredOffers,
        advertises: filteredAdvertises,
        portalRequests: portalAllRequests,
        viewingRouteById,
        offerRouteById,
        advertiseRouteById,
        portalRouteByOrderId
    }), [
        allUsers,
        filteredViewings,
        filteredOffers,
        filteredAdvertises,
        portalAllRequests,
        viewingRouteById,
        offerRouteById,
        advertiseRouteById,
        portalRouteByOrderId
    ])

    const allAttention = useMemo(() => [
        ...offersNotifies,
        ...viewingsNotifies,
        ...advertisesNotifies
    ], [offersNotifies, viewingsNotifies, advertisesNotifies])

    const isThereNotices = useMemo(() => (viewingsNotifies?.length + offersNotifies?.length + advertisesNotifies?.length) > 0,
        [viewingsNotifies?.length, offersNotifies?.length, advertisesNotifies?.length])

    const filtersBottomOffset = useMemo(() => {
        if (isWithSidebar) return undefined
        return userDevice === 'IPhone' ? '9.65rem' : '8.2rem'
    }, [isWithSidebar, userDevice])

    const openFiltersPopup = useCallback(() => {
        dispatch(setShowPopupScheduleFilters(true))
    }, [dispatch])

    const closeFiltersPopup = useCallback(() => {
        dispatch(setShowPopupScheduleFilters(false))
    }, [dispatch])

    const openAgentActivityItem = useCallback((item: AgentActivityTaskItem) => {
        if (item.category === 'adverts') {
            dispatch(setWhereAfterToGo(`/${userTypeLowercase}/schedule`))
        }
        navigate(item.route)
    }, [dispatch, navigate, userTypeLowercase])

    return {
        pathname,
        isDesktop,
        isWithSidebar,
        loading,
        userDevice,
        filterNavigate,
        scheduleFiltersApply,
        setScheduleFiltersApply,
        expanded,
        setExpanded,
        filteredViewings,
        filteredOffers,
        filteredAdvertises,
        viewingsNotifies,
        offersNotifies,
        advertisesNotifies,
        portalViewingRequests,
        portalOfferRequests,
        portalAllRequests,
        agentActivityCards,
        allAttention,
        isThereNotices,
        filtersBottomOffset,
        mainButtonOpen,
        showPopupScheduleFilters,
        openAgentActivityItem,
        openFiltersPopup,
        closeFiltersPopup
    }
}
